import { AddIcon } from '@chakra-ui/icons';
import { Box, Center, Container, HStack, SimpleGrid, Stack, VStack } from '@chakra-ui/react';
import { useScrollToTop } from 'hooks/useScrollToTop';
import { useNavigate } from 'react-router-dom';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import Button from 'components/Button';
import PageLoader from 'components/PageLoader';
import { ProtectedElement } from 'components/ProtectedElement';
import { SEO } from 'components/SEO';
import Text from 'components/Text';
import { useMediaQueries } from 'layout/MediaQueriesProvider';
import { ERoute } from 'types/frontend';

import CVCard from './components/CVCard';
import { useGetCVsQuery } from './hooks/useGetCVsQuery';
import { CV_LIMIT } from './types';
import { canCreateCV, getCVLimitMessage } from './utils/cvValidation';

const CVBuilder = () => {
  useScrollToTop();
  const navigate = useNavigate();
  const { id: currentUserId, isFree } = useCurrentUser();
  const { isMobile } = useMediaQueries();
  const { cvs, isFetching } = useGetCVsQuery(currentUserId);

  const limitMessage = getCVLimitMessage(cvs.length);
  const isLockedUser = isFree;

  const renderEmptyState = () => (
    <Center width="100%" mt={isMobile ? 10 : '12vh'}>
      <VStack spacing={6} maxWidth="420px">
        <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
          <rect x="20" y="10" width="80" height="100" rx="8" fill="#2D3748" stroke="#4A5568" strokeWidth="2" />
          <rect x="32" y="24" width="56" height="4" rx="2" fill="#718096" />
          <rect x="32" y="34" width="40" height="3" rx="1.5" fill="#4A5568" />
          <rect x="32" y="46" width="56" height="3" rx="1.5" fill="#4A5568" />
          <rect x="32" y="53" width="48" height="3" rx="1.5" fill="#4A5568" />
          <rect x="32" y="60" width="52" height="3" rx="1.5" fill="#4A5568" />
          <rect x="32" y="74" width="30" height="4" rx="2" fill="#718096" />
          <rect x="32" y="84" width="56" height="3" rx="1.5" fill="#4A5568" />
          <rect x="32" y="91" width="44" height="3" rx="1.5" fill="#4A5568" />
          <circle cx="95" cy="95" r="20" fill="#6B46C1" opacity="0.2" />
          <path d="M88 95h14M95 88v14" stroke="#6B46C1" strokeWidth="3" strokeLinecap="round" />
        </svg>
        <Text fontSize="xLarge" fontWeight="regular" textAlign="center">
          Create your first professional CV
        </Text>
        <Text fontSize="small" opacity="secondary" textAlign="center">
          AI-powered, ATS-optimized CVs designed for developer roles. Pick a role and let AI generate
          the perfect structure, or upload an existing PDF.
        </Text>
        <ProtectedElement
          leftIcon={<AddIcon boxSize={3} />}
          onClick={() => navigate(`${ERoute.CVBuilder}${ERoute.NewCV}`)}
          isDisabled={!canCreateCV(cvs.length)}
          scheme="gradient"
          size="lg"
        >
          New CV
        </ProtectedElement>
      </VStack>
    </Center>
  );

  const renderAddCard = () => (
    <Box
      borderRadius="lg"
      border="1px solid"
      borderColor="whiteAlpha.300"
      backgroundColor="whiteAlpha.50"
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="200px"
      cursor={canCreateCV(cvs.length) ? 'pointer' : 'not-allowed'}
      opacity={canCreateCV(cvs.length) ? 0.8 : 0.3}
      _hover={canCreateCV(cvs.length) ? { borderColor: 'purple.400', backgroundColor: 'whiteAlpha.100' } : {}}
      transition="all 0.2s"
      onClick={() => canCreateCV(cvs.length) && navigate(`${ERoute.CVBuilder}${ERoute.NewCV}`)}
    >
      <VStack spacing={2}>
        <AddIcon boxSize={6} color="whiteAlpha.500" />
        <Text fontSize="small" opacity="secondary" fontWeight="medium">
          New CV
        </Text>
      </VStack>
    </Box>
  );

  const renderMockPreviews = () => {
    const mockTemplates = [
      { name: 'Classic', color: '#2B6CB0', description: 'Single column, traditional layout' },
      { name: 'Modern', color: '#6B46C1', description: 'Two-column layout with sidebar' },
      { name: 'Creative', color: '#C05621', description: 'Accent blocks and visual flair' },
    ];

    return (
      <VStack spacing={8} mt={6}>
        <VStack spacing={2} textAlign="center" maxWidth="500px" mx="auto">
          <Text fontSize="xLarge" fontWeight="semibold">
            Professional CVs, Powered by AI
          </Text>
          <Text fontSize="small" opacity="secondary">
            Create ATS-optimized developer CVs with AI-generated content, multiple templates, and
            PDF export. Upgrade your plan to get started.
          </Text>
        </VStack>
        <SimpleGrid columns={[1, 1, 3]} gap={6} width="100%">
          {mockTemplates.map((mock) => (
            <Box
              key={mock.name}
              borderRadius="lg"
              border="1px solid"
              borderColor="whiteAlpha.100"
              overflow="hidden"
              position="relative"
            >
              <Box height="4px" backgroundColor={mock.color} />
              <Box p={5}>
                {/* Mini mock CV */}
                <Box
                  backgroundColor="white"
                  borderRadius="md"
                  p={4}
                  height="200px"
                  overflow="hidden"
                  opacity={0.8}
                >
                  <Box height="4px" width="50%" backgroundColor={mock.color} borderRadius="full" mb={2} mx={mock.name === 'Classic' ? 'auto' : '0'} />
                  <Box height="3px" width="35%" backgroundColor="gray.300" borderRadius="sm" mb={1} mx={mock.name === 'Classic' ? 'auto' : '0'} />
                  <Box height="2px" width="60%" backgroundColor="gray.200" borderRadius="sm" mb={4} mx={mock.name === 'Classic' ? 'auto' : '0'} />
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Stack key={i} spacing={1} mb={2}>
                      <Box height="3px" width="40%" backgroundColor="gray.300" borderRadius="sm" />
                      <Box height="2px" width="85%" backgroundColor="gray.100" borderRadius="sm" />
                      <Box height="2px" width="70%" backgroundColor="gray.100" borderRadius="sm" />
                    </Stack>
                  ))}
                </Box>
                <VStack mt={3} spacing={1} alignItems="flex-start">
                  <Text fontSize="medium" fontWeight="semibold">
                    {mock.name}
                  </Text>
                  <Text fontSize="small" opacity="secondary">
                    {mock.description}
                  </Text>
                </VStack>
              </Box>
              {/* Lock overlay */}
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                background="linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.6) 100%)"
                display="flex"
                alignItems="flex-end"
                justifyContent="center"
                pb={6}
              >
                <Button
                  scheme="gradient"
                  size="sm"
                  onClick={() => navigate(ERoute.Plans)}
                >
                  Upgrade to Unlock
                </Button>
              </Box>
            </Box>
          ))}
        </SimpleGrid>
      </VStack>
    );
  };

  const renderContent = () => {
    if (isLockedUser) {
      return renderMockPreviews();
    }

    if (isFetching && cvs.length === 0) {
      return <PageLoader shift />;
    }

    if (cvs.length === 0) {
      return renderEmptyState();
    }

    return (
      <SimpleGrid columns={[1, 1, 2, 3]} gap={6} mt={4}>
        {cvs.map((cv) => (
          <CVCard key={cv.id} cv={cv} totalCVs={cvs.length} />
        ))}
        {canCreateCV(cvs.length) && renderAddCard()}
      </SimpleGrid>
    );
  };

  return (
    <Container maxW="1400px" width="100%">
      <SEO
        title="BigDevSoon | CV Builder"
        description="Create professional, ATS-optimized CVs tailored for developer roles with AI-powered content suggestions."
        url={`${process.env.REACT_APP_SITE_URL}cv-builder`}
      />
      <VStack alignItems="flex-start" width="100%" spacing={4}>
        <VStack
          alignItems="flex-start"
          background="gray.900"
          width="100%"
          py={3}
          spacing={4}
        >
          <VStack alignItems="flex-start">
            <HStack spacing={3}>
              <Text fontSize="xxLarge" fontWeight="semibold">
                CV Builder
              </Text>
              <Text fontSize="small" opacity="tertiary">
                {cvs.length}/{CV_LIMIT}
              </Text>
            </HStack>
            <Text fontSize="small" fontWeight="regular" opacity="secondary">
              Create ATS-optimized CVs powered by AI. Pick a role and get started.
            </Text>
          </VStack>
        </VStack>
        {limitMessage && (
          <Text fontSize="small" color="orange.300">
            {limitMessage}
          </Text>
        )}
      </VStack>
      {renderContent()}
    </Container>
  );
};

export default CVBuilder;
